<script setup lang="ts">
import { computed } from 'vue';

import {
  useBrowserStore,
  useRouterStore,
} from '@apps/main/new-desktop/core/stores';
import {
  StayTag,
  HotelStayTagType,
} from '@apps/main/new-desktop/modules/stayTag';
import { CustomersRatingTag } from '@apps/main/new-desktop/modules/customersRating';
import { HotelAmenities } from '@apps/main/new-desktop/modules/hotelAmenities';

import { AppRawButton, AppStars, AppTag, AppRouterLink } from '@libs/ui';

import type {
  AccommodationProductCardContent,
  AccommodationSegmentContext,
} from '../../../models';

interface AccommodationProductCardContentProps {
  productCardContent: AccommodationProductCardContent;
  context: AccommodationSegmentContext;
  printMode: boolean;
}

const props = defineProps<AccommodationProductCardContentProps>();
interface AccommodationProductCardContentEmits {
  (eventName: 'mouse-enter-content'): void;
  (eventName: 'mouse-leave-content'): void;
  (eventName: 'show-details'): void;
  (eventName: 'show-rooms'): void;
}

const emit = defineEmits<AccommodationProductCardContentEmits>();

const browserStore = useBrowserStore();
const routerStore = useRouterStore();

const hasMultipleRooms = computed<boolean>(
  () => props.productCardContent.cardDetails.length > 1
);
</script>

<template>
  <div
    :class="[
      'accommodation-product-card-content',
      { 'print-mode': props.printMode },
    ]"
  >
    <div class="details">
      <AppRawButton
        class="upper-section"
        :class="{
          mobile: browserStore.isSmallerThan600px,
        }"
        @mouseenter="emit('mouse-enter-content')"
        @mouseleave="emit('mouse-leave-content')"
        @focusin="emit('mouse-enter-content')"
        @focusout="emit('mouse-leave-content')"
        @keydown.enter="emit('show-details')"
        @click="emit('show-details')"
      >
        <AppRouterLink
          :link="
            routerStore.getHotelDetailsUrlInTripOverview(
              { ingredientIndex: props.context.ingredientIndex },
              props.context.hotelCode
            )
          "
          font="font-body-m-bold"
          color="color-neutral-70"
          class="name"
        >
          {{ props.productCardContent.header }}
        </AppRouterLink>
        <div v-if="props.productCardContent.subheader" class="location">
          {{ props.productCardContent.subheader }}
        </div>

        <div class="info-wrapper">
          <AppStars :amount="props.productCardContent.rating.stars" size="18" />
          <CustomersRatingTag
            v-if="props.productCardContent.guestRating"
            :guest-rating="props.productCardContent.guestRating"
            font-size="s"
          />
          <StayTag
            v-if="
              props.productCardContent.cancellationPolicies &&
              props.productCardContent.cancellationPolicies.type ===
                HotelStayTagType.FreeCancellation
            "
            class="cancellation-policy"
            :stay-tag="props.productCardContent.cancellationPolicies"
            :wide="browserStore.isSmallerThan1200px"
            short-version
          />
          <AppTag v-if="props.productCardContent.mealName" type="grey" size="s">
            <template #title>
              <span class="tag-text">
                {{ props.productCardContent.mealName }}
              </span>
            </template>
          </AppTag>
        </div>

        <HotelAmenities :amenities="props.productCardContent.amenities" />
      </AppRawButton>
      <AppRawButton
        class="middle-section"
        @click.stop="emit('show-rooms')"
        @mouseenter="emit('mouse-leave-content')"
        @mouseleave="emit('mouse-enter-content')"
        @focusin="emit('mouse-leave-content')"
        @focusout="emit('mouse-enter-content')"
        @keydown.enter="emit('show-rooms')"
      >
        <div
          v-for="element in props.productCardContent.cardDetails"
          :key="element.label + element.description"
          :class="['description', { 'single-room': !hasMultipleRooms }]"
        >
          <span v-if="hasMultipleRooms" class="label">{{ element.label }}</span>
          {{ element.description }}
        </div>
      </AppRawButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.accommodation-product-card-content {
  height: 100%;
  width: 100%;

  &.print-mode {
    .details {
      .upper-section {
        .name {
          @include on-hover {
            cursor: default;
            text-decoration: none;
          }
        }
      }
    }
  }

  @include for-less-or-equal-than-599-px {
    grid-template-rows: auto auto auto;
    grid-template-columns: auto;
  }

  .details {
    display: flex;
    flex-flow: column;
    justify-content: space-between;
    height: 100%;
    gap: var(--spacing-12);

    .upper-section {
      display: grid;
      grid-template-rows: min-content min-content min-content auto;
      gap: var(--spacing-8);
      height: 100%;

      &.mobile {
        gap: 0;

        .info-wrapper {
          margin: var(--spacing-4) 0 var(--spacing-8) 0;
        }
      }

      .name {
        color: var(--color-soft-black);
        display: -webkit-box;
        max-width: var(--card-width);
        line-clamp: 2;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        padding: 0;
        text-decoration: none;

        @include font-label-m-bold;
        @include on-hover {
          cursor: pointer;
          text-decoration: underline;
        }
      }

      .location {
        @include font-label-s-light;
        color: var(--color-neutral-60);
      }

      .info-wrapper {
        display: flex;
        flex-direction: row;
        align-items: center;
        flex-wrap: wrap;
        gap: var(--spacing-8);

        .cancellation-policy {
          width: fit-content;
        }
      }

      .tag-text {
        @include font-label-s-bold;
      }
    }

    .middle-section {
      padding: var(--spacing-12);
      background: transparent;
      border: 1px solid var(--color-scrollbar);
      border-radius: var(--border-radius-8);
      margin: 0 0 var(--spacing-12) 0;

      .description {
        @include font-label-s-light;

        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        width: 0;
        min-width: 100%;

        .label {
          @include font-label-s-bold;
          white-space: nowrap;
        }

        &.single-room {
          white-space: normal;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          line-clamp: 2;
        }
      }
    }
  }

  .price {
    align-self: center;
  }
}
</style>
